// LOAD-BEARING SIDE-EFFECT IMPORT — do not delete as "unused". See the
// `src/hooks/query/integrations/useQueryEhrLaunchPastNotes.ts` delegate on this
// door for the measurement.
import '@/pages/api/scribeAPIV2';

import { integrationMaxAttemptsReachedAtom } from '@/container/integrations/setup-integration/utils';
import { GET_USER_EHR } from '@/hooks/query/integrations/useQueryUserEHR';
import { GET_USER_EHR_LAUNCH_PROFILES } from '@/hooks/query/integrations/useQueryUserEHRLaunchProfiles';
import { trackConnectIntegration } from '@/lib/analytics/integrations/ehr-connect-events';
import { type ErrorResponse } from '@/types';
import { logger } from '@/utils/logger/logger';
import { toast } from '@heidi/design-system/toast';
import { useMutateConnectIntegrationEhrLaunch as useMutateConnectIntegrationEhrLaunchCore } from '@heidi/integrations/ehr/data';
import { useQueryClient } from '@tanstack/react-query';
import { type AxiosError } from 'axios';
import { useSetAtom } from 'jotai';
import { MAX_ATTEMPTS_ERROR_DESCRIPTION } from '../handleIntegrationMutationError';

/**
 * Delegate, tier T2. The request and the cache identity live at
 * `@heidi/integrations/ehr/data`; the two invalidations, the analytics emit, the
 * toast and the max-attempts atom stay here.
 *
 * THE SUCCESS SEQUENCE IS KEPT WHOLE ON PURPOSE. Its two `invalidateQueries`
 * calls are awaited SEQUENTIALLY and then the emit and the toast follow, and one
 * of the two identities — `GET_USER_EHR_LAUNCH_PROFILES` — already lives behind
 * the door, so the package could have invalidated it itself. Doing that would
 * have split four ordered statements across the boundary and put the emit before
 * an await that used to precede it. It is one port instead, so the order stays a
 * property of one file.
 *
 * `GET_USER_EHR` stays where it is declared: its read hook is one of the auth
 * readers whose heal is the SSOT rollout.
 *
 * The `onError` branch is unchanged, `duration: Infinity` included — this fires
 * on a setup screen where the clinician has to contact support, so a toast that
 * auto-dismisses would lose the instruction. The two `logger.error` lines after
 * it are the pre-carve fall-through and are transcribed, trailing
 * `'[INT] ' + undefined` included for a body with no `detail.msg`.
 *
 * Signature unchanged: callers still pass nothing.
 */
export function useMutateConnectIntegrationEhrLaunch() {
  const queryClient = useQueryClient();
  const setIntegrationMaxAttemptsReached = useSetAtom(integrationMaxAttemptsReachedAtom);

  return useMutateConnectIntegrationEhrLaunchCore({
    onIntegrationConnected: async (payload) => {
      await queryClient.invalidateQueries({
        queryKey: [GET_USER_EHR],
      });

      await queryClient.invalidateQueries({
        queryKey: [GET_USER_EHR_LAUNCH_PROFILES],
      });

      trackConnectIntegration(payload);

      toast({
        tone: 'success',
        title: 'Integration connected',
        description: 'Integration connected successfully',
      });
    },
    onConnectIntegrationError: (error) => {
      const errorResponse = (error as AxiosError).response?.data as ErrorResponse;

      if (
        errorResponse?.detail?.type === 'EHRUserHitsDirectIntegrationMaxAttemptsError'
      ) {
        setIntegrationMaxAttemptsReached(true);
        toast({
          duration: Infinity,
          tone: 'destructive',
          title: 'Failed to connect integration',
          description: MAX_ATTEMPTS_ERROR_DESCRIPTION,
        });
        return;
      }

      logger.error('[INT] Failed to connect integration');
      logger.error('[INT] ' + errorResponse?.detail?.msg);
    },
  });
}
